import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Employer, UseQueryWrapper } from 'app/types';
import { useSnakeBar } from 'app/hooks/useSnakeBar';
import type { SearchEmployersParams } from 'app/Domain/Employers/Services/EmployerService';
import { employerService } from 'app/Domain/Employers/Services/EmployerService';

export type SearchEmployersQueryResultData = {
  page: number;
  pageSize: number;
  totalItems: number;
  items: Employer[];
};

export type SearchEmployersQueryVariables = SearchEmployersParams;

const queryFn =
  ({ page, pageSize, searchQuery, sortBy, sortOrder }: SearchEmployersQueryVariables) =>
  async (): Promise<SearchEmployersQueryResultData> => {
    const response = await employerService.searchEmployers({ page, pageSize, searchQuery, sortBy, sortOrder });
    if ('cancelPrevQuery' in response) {
      return { page: 0, pageSize: 0, totalItems: 0, items: [] };
    }
    return response.data as SearchEmployersQueryResultData;
  };

const QUERY_KEY = 'searchEmployers';

export const useEmployersSearchQuery: UseQueryWrapper<
  SearchEmployersQueryResultData,
  SearchEmployersQueryVariables
> = ({ variables, options = {} }) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  return useQuery<SearchEmployersQueryResultData>(
    [
      QUERY_KEY,
      variables.ownerId,
      variables.page,
      variables.pageSize,
      variables.searchQuery,
      variables.sortBy,
      variables.sortOrder,
    ],
    queryFn(variables),
    {
      onError,
      keepPreviousData: true,
      staleTime: 3 * 60 * 1000,
      ...options,
    },
  );
};
